import { ButtonV2, TooltipV2 } from '@cimplico/components';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { useWorkpaperContext } from '../../../../providers/WorkpaperProvider';
import { useOtherScheduleCycle } from '../../../../queries/use-other-schedule-cycle';
import { useTrialBalanceCycle } from '../../../../queries/use-trial-balance-cycle';

type PrevNextProps = {
    worksheetId: string;
};

function PrevNext({ worksheetId }: PrevNextProps): JSX.Element {
    const { id } = useWorkpaperContext();
    const navigate = useNavigate();
    const location = useLocation();
    // Check if is trial balance or other schedules
    const isTrialBalance = location.pathname.includes('trial-balance');
    const savedTBFilters = useSelector((state: RootState) => state.workpaperFilters.value[id]?.trialBalance);
    const savedOSFilters = useSelector((state: RootState) => state.workpaperFilters.value[id]?.otherSchedules);

    let filter = isTrialBalance ? `${savedTBFilters?.filterQuery}` : `${savedOSFilters?.filterQuery}`;
    if (savedTBFilters?.tab && savedTBFilters?.tab !== 'ALL') {
        filter += `&status=${savedTBFilters.tab}`;
    }

    const sort = isTrialBalance ? `${savedTBFilters?.sortQuery}` : `${savedOSFilters?.sortQuery}`;

    const wsCycleQuery = useTrialBalanceCycle({ id: worksheetId, filter, sort }, isTrialBalance);
    const osCycleQuery = useOtherScheduleCycle({ id: worksheetId, filter, sort }, !isTrialBalance);

    const prev = wsCycleQuery.data?.prev ?? osCycleQuery.data?.prev;
    const next = wsCycleQuery.data?.next ?? osCycleQuery.data?.next;

    const isLoading = wsCycleQuery.isLoading || osCycleQuery.isLoading;
    const isError = wsCycleQuery.isError || osCycleQuery.isError;

    return (
        <div className="flex items-center">
            <TooltipV2 disabled={!prev} message={prev?.name} position="left" delayMs={750}>
                <Link to={`../${prev?.id}${location.search}`} className={clsx({ 'pointer-events-none': !prev })}>
                    <ButtonV2
                        size="lg"
                        type="hollow"
                        leftIcon={<ArrowLeftIcon className="text-gray-600" />}
                        isDisabled={isLoading || isError || !prev}
                        shortcutKey="ArrowLeft"
                        requiresAltKey
                        hideShortcutKey
                        onClick={(): void => {
                            navigate(`../${prev?.id}${location.search}`);
                        }}
                    />
                </Link>
            </TooltipV2>
            <TooltipV2 disabled={!next} message={next?.name} delayMs={750}>
                <Link to={`../${next?.id}${location.search}`} className={clsx({ 'pointer-events-none': !next })}>
                    <ButtonV2
                        size="lg"
                        type="hollow"
                        leftIcon={<ArrowRightIcon className="text-gray-600" />}
                        isDisabled={isLoading || isError || !next}
                        shortcutKey="ArrowRight"
                        requiresAltKey
                        hideShortcutKey
                        onClick={(): void => {
                            navigate(`../${next?.id}${location.search}`);
                        }}
                    />
                </Link>
            </TooltipV2>
        </div>
    );
}

export default PrevNext;
